import { Avatar, Box, Button, Tabs, Text } from '@factorialco/deprecated-design-system'
import {
  FilterType,
  ProjectionKind,
  buildProcurementPurchaseRequestsDatatableFilter,
  useProcurementPurchaseRequestsDatatable,
} from 'generated/resources/datatable.graphql'

import DataTable from 'modules/core/components/DataTable'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import useSearchParams from 'modules/core/hooks/useSearchParams'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import buildUrl from 'modules/core/lib/buildUrl'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import { contactFullName } from 'modules/finance/lib/contactsUtils'
import { PurchaseRequestDeadline } from 'modules/procurement/components/purchaseRequests/Deadline'
import { EmptyState } from 'modules/procurement/components/purchaseRequests/EmptyState'
import { PurchaseRequestsInsights } from 'modules/procurement/components/purchaseRequests/PurchaseRequestsInsights'
import { SpendingRequestStatusLabel } from 'modules/procurement/components/purchaseRequests/SpendingRequestStatusLabel'

export enum ApproverStatusViewTab {
  PendingMine = 'pending_mine',
  All = 'all',
}

interface Props {
  onTabChange: (tab: string) => void
  tab: string
}

const PurchaseRequestsDataTable = ({ onTabChange, tab }: Props) => {
  const {
    company,
    access: { locale },
  } = useCurrentSession()
  const { navigation } = useNavigation()

  const { debouncedSearch, search, onSearch } = useDebouncedSearch()

  const { requestedAtFrom, requestedAtTo } = useSearchParams<{
    requestedAtFrom?: string
    requestedAtTo?: string
  }>()

  const source = useProcurementPurchaseRequestsDatatable(
    [
      {
        identifier: 'description',
        title: i18n.t('procurement.purchase_requests.list.description'),
        kind: ProjectionKind.Custom,
        template: '{{ description }}',
        render: ({ description }) => {
          return (
            <Box minWidth="s120">
              <Text.Normal size="200" weight="medium">
                {description}
              </Text.Normal>
            </Box>
          )
        },
        sortBy: 'description',
      },
      {
        identifier: 'status',
        title: i18n.t('procurement.purchase_requests.list.status'),
        fixed: true,
        kind: ProjectionKind.Custom,
        template: '{{ status }}',
        render: ({ status }) => {
          return <SpendingRequestStatusLabel status={status} />
        },
        sortBy: 'status',
      },
      {
        identifier: 'cost',
        kind: ProjectionKind.Custom,
        title: i18n.t('procurement.purchase_requests.list.cost'),
        template: '{{ cost.cents }} {{ cost.currency }}',
        textAlign: 'right',
        justifyContent: 'flexEnd',
        render: ({ cost }) => {
          return (
            <Box alignItems="flexEnd">
              {cost && (
                <Text.Normal size="200" weight="medium">
                  {MoneyWithCurrency({
                    amount: Number(cost.cents),
                    currency: cost.currency,
                    locale,
                  })}
                </Text.Normal>
              )}
            </Box>
          )
        },
      },
      {
        identifier: 'date',
        title: i18n.t('procurement.purchase_requests.list.requested'),
        kind: ProjectionKind.Custom,
        template: '{{ date }}',
        render: ({ date }) => {
          return (
            <Box minWidth="s96">
              <Text.Normal size="200">{dateBackendDateTimeToUI(date)}</Text.Normal>
            </Box>
          )
        },
        sortBy: 'date',
      },
      {
        identifier: 'deadline',
        title: i18n.t('procurement.purchase_requests.list.deadline'),
        kind: ProjectionKind.Custom,
        template: '{{ deadline }}',
        render: ({ deadline, status }) => {
          return (
            <Box minWidth="s96">
              <PurchaseRequestDeadline deadline={deadline} status={status} />
            </Box>
          )
        },
        sortBy: 'deadline',
      },
      {
        identifier: 'requesterAccess',
        title: i18n.t('procurement.purchase_requests.list.employee'),
        kind: ProjectionKind.Custom,
        template: '{{ requesterAccess.fullName }}',
        render: ({ requesterEmployee }) => {
          return (
            <Box flexDirection="row" alignItems="center" gap="s16" minWidth="s96">
              <Avatar
                size="s40"
                url={buildUrl(requesterEmployee?.avatar?.url)}
                label={requesterEmployee?.fullName || ''}
              />
              <Text.Normal size="200" weight="medium">
                {requesterEmployee?.fullName || ''}
              </Text.Normal>
            </Box>
          )
        },
      },
      {
        identifier: 'vendor',
        title: i18n.t('procurement.purchase_requests.list.vendor'),
        kind: ProjectionKind.Custom,
        template: '{{ vendor.legalName }}',
        render: ({ vendor }) => {
          return <Text.Normal size="200">{contactFullName(vendor)}</Text.Normal>
        },
      },
      {
        identifier: 'detail',
        kind: ProjectionKind.Decorative,
        render: ({ id, status }) => (
          <Button.Router
            type="secondary"
            to={navigation.procurement.purchaseRequests.detail.fullPath({ id: id })}
            iconName="arrow-right"
            onClick={() =>
              track(trackingEvents.procurement.viewPurchaseRequestDetail, {
                page: 'Purchase Requests',
                status: status,
              })
            }
          />
        ),
        width: 80,
      },
    ],
    {
      variables: {
        companyId: company.id,
        search: debouncedSearch,
        onlyMyPendingApprovals: tab === ApproverStatusViewTab.PendingMine ? true : false,
      },
    }
  )

  const statusFilter = buildProcurementPurchaseRequestsDatatableFilter(source, {
    identifier: 'status',
    title: i18n.t('procurement.purchase_requests.list.status'),
    path: 'status',
    default: true,
    type: FilterType.String,

    categories: [
      {
        kind: 'MultipleChoice',
        operation: 'in',
        options: [
          {
            label: i18n.t('procurement.purchase_requests.status.pending'),
            value: 'pending',
          },
          {
            label: i18n.t('procurement.purchase_requests.status.approved'),
            value: 'approved',
          },
          {
            label: i18n.t('procurement.purchase_requests.status.rejected'),
            value: 'rejected',
          },
        ],
      },
    ],
  })

  const requestDateFilter = buildProcurementPurchaseRequestsDatatableFilter(source, {
    identifier: 'date',
    title: i18n.t('procurement.purchase_requests.list.requested'),
    path: 'date',
    default: !!requestedAtFrom && !!requestedAtTo,
    type: FilterType.Date,
    categories: [
      {
        kind: 'DatePicker',
        operation: 'between',
        value: !!requestedAtFrom && !!requestedAtTo ? [requestedAtFrom, requestedAtTo] : undefined,
      },
    ],
  })

  const approverFilter = buildProcurementPurchaseRequestsDatatableFilter(source, {
    type: FilterType.Entity,
    categories: [
      {
        kind: 'Employee',
        operation: 'in',
      },
    ],
    identifier: 'approvers_employees_ids',
    path: 'approversEmployeesIds',
    title: i18n.t('procurement.purchase_requests.filter.approver'),
    default: true,
  })

  const filters =
    tab == ApproverStatusViewTab.All
      ? [statusFilter, approverFilter, requestDateFilter]
      : [statusFilter, requestDateFilter]

  const companyId = company.id

  return (
    <>
      <Tabs
        currentId={tab}
        onTabClicked={(tab) => {
          onTabChange(tab)
        }}
      >
        <Tabs.Tab
          id={ApproverStatusViewTab.All}
          label={i18n.t('procurement.purchase_requests.tabs.all')}
        />
        <Tabs.Tab
          id={ApproverStatusViewTab.PendingMine}
          label={i18n.t('procurement.purchase_requests.tabs.pending_mine')}
        />
      </Tabs>
      <Box paddingTop="s12">
        <DataTable.Provider
          source={source}
          persistFilters={false}
          pagination={{
            kind: 'offset',
            itemsPerPage: 25,
          }}
          filters={filters}
          sort={{
            path: 'date',
            direction: 'desc',
          }}
        >
          <Box gap="s16">
            <DataTable.HeadBar
              disableDefaultActions
              search={{
                placeholder: i18n.t('common.search'),
                value: search,
                onChange: onSearch,
              }}
            />
            {companyId && (
              <PurchaseRequestsInsights
                companyId={companyId}
                onlyMyPendingApprovals={tab === ApproverStatusViewTab.PendingMine}
                search={search}
              />
            )}
            <DataTable.Table
              autoWidth
              emptyState={
                <EmptyState
                  description={i18n.t('procurement.purchase_requests.empty.description_company')}
                />
              }
            />
          </Box>
        </DataTable.Provider>
      </Box>
    </>
  )
}

export default PurchaseRequestsDataTable
